import React from 'react';
import { AlertCircle } from 'lucide-react';
import { API } from '../../../lib/api';
import { esPregunta, urlImagen, youtubeId } from '../../../lib/formularios';
import { CampoFecha, CampoHora, CampoParrafo, CampoTextoCorto } from './CamposTexto';
import { CampoCasillas, CampoDesplegable, CampoOpcionMultiple } from './CamposEleccion';
import { CampoCalificacion, CampoCuadricula, CampoEscala } from './CamposEscala';
import SubidaArchivos from './SubidaArchivos';

/*
 * Una tarjeta del formulario: pregunta (con su campo), bloque de título, imagen o vídeo.
 * La usan la página para responder, la revisión de la calificación y la vista individual de
 * respuestas (en lectura), así que una pregunta se ve igual en los tres sitios.
 *
 * Props de lectura:
 *   soloLectura  — campos deshabilitados que muestran la respuesta guardada
 *   marcas       — { correctas } para pintar aciertos y errores (cuestionarios)
 *   encabezadoExtra / pie — nodos que añade quien la usa (puntos, retroalimentación, calificar)
 */

const CAMPOS = {
  texto_corto: CampoTextoCorto,
  parrafo: CampoParrafo,
  opcion_multiple: CampoOpcionMultiple,
  casillas: CampoCasillas,
  desplegable: CampoDesplegable,
  escala: CampoEscala,
  calificacion: CampoCalificacion,
  cuadricula_opcion: CampoCuadricula,
  cuadricula_casillas: CampoCuadricula,
  fecha: CampoFecha,
  hora: CampoHora,
};

// Tipos cuyo campo es un solo control: la etiqueta apunta a él. El resto son grupos.
const UN_CONTROL = new Set(['texto_corto', 'parrafo', 'desplegable', 'fecha', 'hora']);

function ImagenItem({ imagen, alt }) {
  if (!imagen?.id) return null;
  return (
    <div className="fr-item-imagen">
      <img className="fm-imagen" src={urlImagen(imagen.id, API)} alt={alt || ''} loading="lazy" />
    </div>
  );
}

export default function TarjetaItem({
  item, valor, onCambio, error, soloLectura = false, semilla, token, vistaPrevia = false,
  onSubiendo, marcas, encabezadoExtra, pie, enlaceArchivo, mostrarPuntos = false, className = '',
}) {
  if (item.tipo === 'titulo') {
    return (
      <div className={`fm-tarjeta fr-bloque-titulo ${className}`} id={`pregunta-${item.id}`}>
        {item.titulo && <h2 className="fr-bloque-titulo-texto">{item.titulo}</h2>}
        {item.descripcion && <div className="fm-descripcion">{item.descripcion}</div>}
      </div>
    );
  }
  if (item.tipo === 'imagen') {
    return (
      <figure className={`fm-tarjeta fr-bloque-imagen ${className}`} id={`pregunta-${item.id}`}>
        {item.titulo && <figcaption className="fm-titulo-pregunta fr-bloque-pie">{item.titulo}</figcaption>}
        <ImagenItem imagen={item.imagen} alt={item.titulo} />
      </figure>
    );
  }
  if (item.tipo === 'video') {
    const id = item.video?.youtube_id || youtubeId(item.video?.url);
    return (
      <div className={`fm-tarjeta fr-bloque-video ${className}`} id={`pregunta-${item.id}`}>
        {item.titulo && <div className="fm-titulo-pregunta">{item.titulo}</div>}
        {id ? (
          <div className="fm-video">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${id}`}
              title={item.titulo || 'Vídeo'}
              loading="lazy"
              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <p className="fm-texto-2">Vídeo no disponible</p>
        )}
        {item.descripcion && <div className="fm-descripcion fr-video-pie">{item.descripcion}</div>}
      </div>
    );
  }
  if (!esPregunta(item)) return null;

  const idTitulo = `fr-titulo-${item.id}`;
  const idError = `fr-error-${item.id}`;
  const idCampo = `fr-campo-${item.id}`;
  const aria = {
    'aria-labelledby': idTitulo,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error ? idError : undefined,
    'aria-required': item.obligatoria && !soloLectura ? true : undefined,
  };
  const puntos = mostrarPuntos && item.puntos ? item.puntos * (item.tipo.startsWith('cuadricula') ? (item.filas || []).length : 1) : 0;

  const cuerpo = item.tipo === 'archivo' ? (
    <SubidaArchivos
      item={item}
      valor={valor}
      onCambio={onCambio}
      soloLectura={soloLectura}
      token={token}
      deshabilitado={vistaPrevia}
      onSubiendo={onSubiendo}
      aria={aria}
      enlaceArchivo={enlaceArchivo}
    />
  ) : (() => {
    const Campo = CAMPOS[item.tipo];
    return Campo ? (
      <Campo
        item={item}
        valor={valor}
        onCambio={onCambio}
        soloLectura={soloLectura}
        semilla={semilla}
        marcas={marcas}
        idCampo={idCampo}
        aria={aria}
      />
    ) : null;
  })();

  const Titulo = UN_CONTROL.has(item.tipo) && !soloLectura ? 'label' : 'div';

  return (
    <div
      className={`fm-tarjeta fr-pregunta ${error ? 'is-error' : ''} ${className}`}
      id={`pregunta-${item.id}`}
    >
      <div className="fr-pregunta-cabeza">
        <Titulo className="fm-titulo-pregunta fr-pregunta-titulo" id={idTitulo} {...(Titulo === 'label' ? { htmlFor: idCampo } : {})}>
          {item.titulo || 'Pregunta sin título'}
          {item.obligatoria && !soloLectura && <span className="fm-obligatoria" aria-hidden="true">*</span>}
        </Titulo>
        {puntos > 0 && !encabezadoExtra && (
          <span className="fr-puntos">{puntos} {puntos === 1 ? 'punto' : 'puntos'}</span>
        )}
        {encabezadoExtra}
      </div>
      {item.descripcion && <div className="fm-descripcion fr-pregunta-descripcion">{item.descripcion}</div>}
      <ImagenItem imagen={item.imagen} alt={item.titulo} />
      <div className="fr-pregunta-campo">{cuerpo}</div>
      {error && (
        <div className="fm-error-texto" id={idError} role="alert">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
      {pie}
    </div>
  );
}
